import { Fragment, h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import './transcript.scss';
import { getHTML } from '../../base/domManager';
import { DATA_EVENTS, INTERNAL_EVENTS, SDK_EVENTS } from '../../../constants/events.cnst';
import fetchInstance from '../../../utils/interceptor';
import { ClassMapConstanst, IdMapConstant, ProjConstants, RenderResponseType, Tabs } from '../../../constants/projConsts';
import { ERROR_CODES } from '../../../constants/errorcodes';

interface TranscriptProps {
    hostInstance: any;
}

interface MessageData {
    timestamp: string | number;
    timestampValue: string | number;
    msg: string;
    from: string;
    type: string;
    isFromSocket?: boolean;
    messageId?: string;
}

export function BulbCount (props : any) {
    return (
        <div className={`intent-suggestion-buld aa-transcript-bulb-${props.msgData?.uuid}`} title={props?.msgData?.value}>
            <sub className={`aa-transcript-bulb-count`}>{props?.msgData?.bulbCount}</sub >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M13.0496 4.64998C13.0496 4.07008 12.5795 3.59998 11.9996 3.59998C11.4197 3.59998 10.9496 4.07008 10.9496 4.64998V5.69998C10.9496 6.27987 11.4197 6.74998 11.9996 6.74998C12.5795 6.74998 13.0496 6.27987 13.0496 5.69998V4.64998Z" fill="#2B75E4" />
                <path d="M17.9393 7.54515C18.3493 7.1351 18.3493 6.47027 17.9393 6.06022C17.5292 5.65017 16.8644 5.65017 16.4543 6.06022L15.7119 6.80269C15.3018 7.21274 15.3018 7.87756 15.7119 8.28761C16.1219 8.69766 16.7868 8.69766 17.1968 8.28761L17.9393 7.54515Z" fill="#2B75E4" />
                <path d="M20.3996 12C20.3996 12.5799 19.9295 13.05 19.3496 13.05H18.2996C17.7197 13.05 17.2496 12.5799 17.2496 12C17.2496 11.4201 17.7197 10.95 18.2996 10.95H19.3496C19.9295 10.95 20.3996 11.4201 20.3996 12Z" fill="#2B75E4" />
                <path d="M6.80231 8.28763C7.21236 8.69768 7.87718 8.69768 8.28723 8.28763C8.69728 7.87758 8.69728 7.21275 8.28723 6.8027L7.54477 6.06024C7.13472 5.65019 6.4699 5.65019 6.05984 6.06024C5.64979 6.47029 5.64979 7.13512 6.05984 7.54517L6.80231 8.28763Z" fill="#2B75E4" />
                <path d="M6.74961 12C6.74961 12.5799 6.27951 13.05 5.69961 13.05H4.64961C4.06971 13.05 3.59961 12.5799 3.59961 12C3.59961 11.4201 4.06971 10.95 4.64961 10.95H5.69961C6.27951 10.95 6.74961 11.4201 6.74961 12Z" fill="#2B75E4" />
                <path d="M9.89961 18.3V17.25H14.0996V18.3C14.0996 19.4598 13.1594 20.4 11.9996 20.4C10.8398 20.4 9.89961 19.4598 9.89961 18.3Z" fill="#2B75E4" />
                <path d="M14.1005 16.2C14.1159 15.8426 14.3176 15.5214 14.6003 15.2982C15.5744 14.529 16.1996 13.3375 16.1996 12C16.1996 9.68038 14.3192 7.79998 11.9996 7.79998C9.68001 7.79998 7.79961 9.68038 7.79961 12C7.79961 13.3375 8.42483 14.529 9.39896 15.2982C9.68164 15.5214 9.88335 15.8426 9.89867 16.2H14.1005Z" fill="#2B75E4" />
            </svg>
        </div>
    )
}

export function Transcript({ hostInstance }: TranscriptProps) {

    const [messages, setMessages] = useState<MessageData[]>([]);
    const [userAgentHistory, setUserAgentHistory] = useState<any[]>([]);
    const [userBotHistory, setUserBotHistory] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(undefined);
    const isInitialRender = useRef(true);
    const rootServiceInstance = hostInstance.rootService;
    const classMapConstansts: any = ClassMapConstanst;
    const assistEnabled = rootServiceInstance?.tabsNTitles?.find((tab: any) => tab.type === Tabs.ASSIST)?.isEnabled;
    const languageData = hostInstance?.languagedata

    // Socket and History API call methods
    useEffect(() => {
        const transcriptEle = hostInstance?.chatEle?.querySelector('.aa-transcript-body-wrapper') as HTMLElement;
        hostInstance.renderIndividualLoader(transcriptEle);
        transcriptHistoryAPICall()
        listeningToSocketEvents();
        return() => {
            hostInstance.off('AGENT_AI_RESPONSE', updateAgentAssistResponse);
            hostInstance.off('AGENT_TRANSCRIPT_MSG', handleUserAgentMessage);
            hostInstance.off('USER_TRANSCRIPT_MSG', handleUserAgentMessage);
            hostInstance.off('AGENT_TRANSCRIPT_RESPONSE', handleUserAgentMessage);
            hostInstance.off('USER_TRANSCRIPT_RESPONSE', handleUserAgentMessage);
            hostInstance.off('AGENT_AI_RESPONSE', updateAgentAssistResponse);
            hostInstance.off(DATA_EVENTS.CALL_CONVERSATION_SUGGESTIONS, handleAssistSuggestions);
            hostInstance.off(INTERNAL_EVENTS.FETCH_TRANSCRIPT, transcriptHistoryAPICall);
        }
    }, []);

    // User and Agent transcript messages from Socket
    const handleUserAgentMessage = (agentResponse: any) => handleTranscriptionResponse(agentResponse);

    const listeningToSocketEvents =() => {

        hostInstance.on('AGENT_TRANSCRIPT_MSG', handleUserAgentMessage);
        hostInstance.on('USER_TRANSCRIPT_MSG', handleUserAgentMessage);

        hostInstance.on('AGENT_TRANSCRIPT_RESPONSE', handleUserAgentMessage);
        hostInstance.on('USER_TRANSCRIPT_RESPONSE', handleUserAgentMessage);
        hostInstance.on('AGENT_AI_RESPONSE', updateAgentAssistResponse);
        hostInstance.on(DATA_EVENTS.CALL_CONVERSATION_SUGGESTIONS, handleAssistSuggestions);
        hostInstance.on(INTERNAL_EVENTS.FETCH_TRANSCRIPT, transcriptHistoryAPICall);

    }

    const updateAgentAssistResponse = (data: any) => {
        const responseType = rootServiceInstance.getTypeOfResponse(data);
        if(responseType === RenderResponseType.SUGGESTIONS && !assistEnabled){
            hostInstance.emit(DATA_EVENTS.CALL_CONVERSATION_SUGGESTIONS, {data : data});
        }
    };

    const handleAssistSuggestions  = (response : any) => {
        if(response?.data && Object.keys(response?.data)?.length > 0){
            let data = response?.data;
            let bulbCount = (data.suggestions.dialogs?.length || 0) + (data.suggestions.faqs?.length || 0) + (data.suggestions.searchassist?.snippets?.length || 0) + (rootServiceInstance.formatSearchResponse(data, false, response.suggestionIndex, ProjConstants.ASSIST)?.articles?.length || 0);
            setMessages((prevResponse : any) => {                
                let returnResponse = [...prevResponse];
                let userBubbleIndex = prevResponse.findLastIndex((data : any) => data.type == 'user');
                if(userBubbleIndex !== -1){
                    returnResponse[userBubbleIndex].count = bulbCount;
                    returnResponse[userBubbleIndex].suggestionUUID = response.uuid;
                    showCountBulb(bulbCount, response, userBubbleIndex);
                }
                return returnResponse;
            })

        }
    }

    const showCountBulb = (bulbCount : number, response : any, bubbleIndex : number) => {
        let uuid = response.uuid;
        const transcriptBulbHtml = getHTML(BulbCount, {bulbCount, uuid, value : response?.data?.value}, hostInstance);
        hostInstance.emit(SDK_EVENTS.BEFORE_VIEW_INIT, transcriptBulbHtml, { event: SDK_EVENTS.TRANSCRIPT_BULB, data: {bulbCount, response, bubbleIndex} });
        let transcriptBubble = hostInstance?.chatEle?.querySelector(`${ClassMapConstanst.TRANSCRIPT_BUBBLE}${bubbleIndex}`);        
        transcriptBubble.querySelector(`.bot-bubble-content .bubble-msg-with-img`).append(transcriptBulbHtml);
        hostInstance.eventManager?.addEventListener(`${ClassMapConstanst.TRANSCRIPT_BULB}${uuid}`, 'click', () => {
            let assistSuggestions : any = hostInstance?.chatEle.querySelector(`#${IdMapConstant.ASSIST_SUGGESTIONS}${uuid}`);
            if(assistSuggestions && assistEnabled){
                transcriptBubble.querySelector(`.aa-transcript-bulb-count`).innerHTML = rootServiceInstance.sanitizeHTML("&#10004;");
                const footerTag = hostInstance.chatEle.querySelector(classMapConstansts.FOOTER);
                hostInstance.openAssistTab(footerTag, true);
                setTimeout(() => {
                    assistSuggestions.scrollIntoView();
                }, 0);
            }
        }, transcriptBubble);
    }

    // Transcript History API call
    const transcriptHistoryAPICall = () => {
        const transcriptEle = hostInstance?.chatEle.querySelector('.aa-transcript-body-wrapper') as HTMLElement;
        fetchTranscriptHistoryData().then((res: any) => {
            hostInstance.removeIndividualLoader(transcriptEle);
            if (res) {
                transcriptEle.innerHTML = '';
                const { userBotMessages, summary, userAgentMessages } = res;
                const processedUserBotMessages = processMessages(userBotMessages?.result);
                setUserBotHistory(processedUserBotMessages);
                if (summary?.message) {
                    summaryBannerData(summary);
                }
                const processedUserAgentMessages = processMessages(userAgentMessages?.result);
                setUserAgentHistory(processedUserAgentMessages);
                setTimeout(() => {
                    rootServiceInstance.scrollBottomTranscript(hostInstance);  
                },);
            } 
        })
        .catch((error: any) => {
            hostInstance.emit(SDK_EVENTS.ON_ERROR, ERROR_CODES['ERR_SUMMARY_LOAD_FAILURE']);
            hostInstance.removeIndividualLoader(transcriptEle);
            addAgentJoiningBanner();
        });
         
    }

    useEffect(() => {
        if(isInitialRender.current){
            isInitialRender.current = false;
            return;
        }
        (userBotHistory || []).forEach((element: any) => {
            if (element?.components[0]?.data?.text) {
                handleUserBotHistoryResponse(element);
            }
        });
        if (summary?.msg) {
            const summaryBanner = getHTML(SummaryEleBanner, summary, hostInstance);
            const transcriptEle = hostInstance?.chatEle.getElementsByClassName(classMapConstansts.TRANSCRIPT_BODY_WRAPPER)?.[0];
            hostInstance.emit(SDK_EVENTS.BEFORE_VIEW_INIT, summaryBanner, { event: SDK_EVENTS.SUMMARY_TEMPLATE, summary });
            transcriptEle?.append(summaryBanner);
        }
        addAgentJoiningBanner();
        (userAgentHistory || []).forEach((element: any) => {
            if ((element?.msgType === 'USER' || element?.msgType === 'AGENT') && element?.components[0]?.data?.text) {
                handleUserAgentHistoryResponse(element);
            }
        });
    }, [userBotHistory, userAgentHistory, summary]);

    const processMessages = (messages: any[]) => {
        if(!messages?.length){
            return [];
        }
        return messages.map(item => {
            try {
                if (typeof item?.components[0]?.data?.text === 'string') {
                    const parsedText = JSON.parse(item.components[0].data.text);
                    if (parsedText?.payload?.text) {
                        item.components[0].data.text = parsedText.payload.text;
                    }
                }
            } catch {
                // Handle JSON parsing error if needed
            }
            return item;
        });
    };

    const fetchTranscriptHistoryData: any = () => {
        let isSummaryRequired = true;
        const {botId, conversationId, channel, sessionId} = rootServiceInstance.connectionDetails || {};
        let params = {
            conversationId,
            botId,
            channel,
            sessionId: sessionId || 0,
            isSummaryRequired: isSummaryRequired,
            pId: rootServiceInstance.internalTransferData?.participant?.identity || ''
        }
        return fetchInstance('get_history_trascript', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }, hostInstance, params
        );


    };

    // userBot Converstion Transcript History
    const handleUserBotHistoryResponse = (response: any) => {

        const data: MessageData = {
            timestamp: formatStandardTime(response.createdOn),
            timestampValue: response.timestampValue,
            msg: response.components[0]?.data?.text,
            from: response.msgType === 'BOT' ? 'bot' : 'user',
            type: response.msgType === 'BOT' ? 'bot' : 'user',
            isFromSocket: false,
            messageId: response?._id
        };

        const trnsptBubble = getHTML(TranscriptBubble, {data}, hostInstance);
        const transcriptEle = hostInstance?.chatEle.getElementsByClassName(classMapConstansts.TRANSCRIPT_BODY_WRAPPER)?.[0];
        hostInstance.emit(SDK_EVENTS.BEFORE_VIEW_INIT, trnsptBubble, {
            event: response.msgType === 'BOT' ? SDK_EVENTS.BOT_TRANSCRIPT : SDK_EVENTS.USER_TRANSCRIPT,
            data
        });
        transcriptEle.append(trnsptBubble);
    }

    // userAgent Conversation Transcript History
    const handleUserAgentHistoryResponse = (response: any) => {
        const data: MessageData = {
            timestamp: formatStandardTime(response.createdOn),
            timestampValue: response.timestampValue,
            msg: response.components[0]?.data?.text,
            from: response.msgType === 'AGENT' ? 'agent' : 'user',
            type: response.msgType === 'AGENT' ? 'agent' : 'user',
            isFromSocket: false,
            messageId: response?._id
        };

        const trnsptBubble = getHTML(TranscriptBubble, {data}, hostInstance);
        const transcriptEle = hostInstance?.chatEle.getElementsByClassName(classMapConstansts.TRANSCRIPT_BODY_WRAPPER)?.[0];
        hostInstance.emit(SDK_EVENTS.BEFORE_VIEW_INIT, trnsptBubble, {
            event: response.msgType === 'AGENT' ? SDK_EVENTS.AGENT_TRANSCRIPT : SDK_EVENTS.USER_TRANSCRIPT,
            data
        });
        transcriptEle?.append(trnsptBubble);
    }

    // conversion of standard UTC timestamp
    const formatStandardTime = (timeStamp: any) => {
        const date = new Date(timeStamp);
        let hours = date.getHours();
        let minutes: any = date.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
    
        hours = hours % 12;
        hours = hours ? hours : 12;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        
        return `${hours}:${minutes} ${ampm}`;
    }

    // Live conversation of user and Agent Transcripts
    const handleTranscriptionResponse = (response: any) => {
        const type = response?.author?.type?.toLowerCase()
        const data: MessageData = {
            timestamp: formatAmpm(new Date()),
            timestampValue: new Date().getTime(),
            msg: response?.value,
            from: type,
            type,
            isFromSocket: true,
            messageId: response?._id
        };

        setMessages((prevResponse : any) => {
            let messages = [...prevResponse];
            messages?.push(data);            
            const trnsptBubble = getHTML(TranscriptBubble, {data, index : messages?.length - 1}, hostInstance) ;
            const transcriptEle = hostInstance?.chatEle.getElementsByClassName(classMapConstansts.TRANSCRIPT_BODY_WRAPPER)?.[0];
            hostInstance.emit(SDK_EVENTS.BEFORE_VIEW_INIT, trnsptBubble, {
                event: response?.author?.type === 'AGENT' ? SDK_EVENTS.AGENT_TRANSCRIPT : SDK_EVENTS.USER_TRANSCRIPT, 
                data
            });
            transcriptEle?.append(trnsptBubble);
            return messages;
        })
    }

    // Live converstion timestamp conversion
    const formatAmpm = (date: any) => {
        let hours = date.getHours();
        let minutes = date.getMinutes();
        let ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        let strTime = hours + ':' + minutes + ' ' + ampm;
        return strTime;
      }

    // Banner for the Agent Joining
    const addAgentJoiningBanner = () => {
        const transcriptEle = hostInstance?.chatEle.getElementsByClassName(classMapConstansts.TRANSCRIPT_BODY_WRAPPER)?.[0];
        const isAgentBannerPresent = transcriptEle?.querySelector('.agent-joined-banner');
        if(isAgentBannerPresent){
            return;
        }
        const timeStr = formatAmpm(new Date());
        const data = { title: `${languageData?.AGENT_JOINED_CONV}`, timeStamp: timeStr };
        const agentJoinBanner = getHTML(AgentJoiningEleBanner, data, hostInstance);
        hostInstance.emit(SDK_EVENTS.BEFORE_VIEW_INIT, agentJoinBanner, {
            event: SDK_EVENTS.AGENT_JOINED_TEMPLATE, data
        });
        transcriptEle?.append(agentJoinBanner);
    }

    // Agent joining banner HTML
    const AgentJoiningEleBanner = (props: any) => {
        const {msgData} = props;
        return (
            <div className="agent-joined-banner">
                <div className="line-border"></div>
                <div className="info-joined-details">
                    <h4 className="aa-text-sm">{msgData.title}</h4>
                    {/* <p className="aa-desc-xs">{msgData.timeStamp}</p> */}
                </div>
                <div className="line-border"></div>
            </div>
        )
    }

    
    const SummaryEleBanner = (props: any) => {
        const {msgData} = props;
        return (
            <div className="summary-wrapper">
                <div className="summary-header">
                    <i className="krIocnsFont-Aimark"></i>
                    <span className="aa-text-md medium">{msgData.title}</span>
                </div>
                <p className="aa-text-sm">{msgData.msg}</p>
            </div>
        )
    }

    const summaryBannerData = (response: any) => {
        let data: any = {
            title : 'Conversation Summary',
            msg : response?.message
        }
        setSummary(data);
    }

    // Message Bubbles for user agent
    const TranscriptBubble = (props: any)=>{
        const {msgData} = props;
        const actualData = props?.msgData?.data || {};        
        return (
            <div className={`aa-transcript-bubble-${msgData.index} ${actualData.type === 'user' ? 'aa-bot-bubble-comp' : 'aa-agent-bubble-comp'}`}>
                <div className={actualData.type === 'user' ? 'bot-bubble-content' : 'agent-bubble-content'}>
                    <div className="top-info">
                        {actualData.type !== 'bot' && (<div className="you-text">{actualData.type === 'agent' ? 'agent' : 'user'}</div>)}
                        {actualData.type === 'bot' && (<div className="you-text">{actualData.type === 'bot' ? 'bot' : 'user'}</div>)}
                        <div className="time-tamp">
                            <time>{actualData.timestamp}</time>
                        </div>
                    </div>
                    <div className="bubble-msg-with-img">
                        <div className="bubble-msg" dangerouslySetInnerHTML={{ __html: (actualData.msg) }}></div>
                    </div>
                </div>
            </div>
            
        );
    };

    //scroll to bottom during message incoming
    useEffect(() => {
        rootServiceInstance.scrollBottomTranscript(hostInstance);
    }, [messages]);


    return (
        <Fragment>             
            <div className="transcript-template-wrapper">
                <div className="transcript-template-data">
                    <h1 className="transcript-header aa-text-md semibold">{languageData?.TRANSCRIPT}</h1>
                    <div className="transcript-body-wrapper aa-transcript-body-wrapper">
                    </div>
                </div>
            </div>
        </Fragment>
    );
}
